// =========================
// DashboardView — BL-039 (Investor Intelligence Dashboard, Phase 1)
//
// PURE presentational surface for the investor command center. Given a
// `metrics` object (from /get-dashboard-metrics) and the user `profile` (for
// ADVISORY entitlement gating only), it renders:
//   • Activity & analysis metrics            (Story 39.2)
//   • Pursue / Investigate / Reject signals  (Story 39.3)
//   • Opportunity pipeline by status         (Story 39.4)
//   • User-derived market hotspots           (Story 39.5)
//   • Review-next + trial usage + empty state(Story 39.6)
//
// ADR-044: every value is a SIGNAL about the investor's OWN activity — never
// advice. The surface is framed as "Opportunity Signals" and carries a
// not-advice clarifier. No engine internals are exposed. Design tokens only
// (no hex). Server remains the boundary; entitlement gating here is UX only.
// =========================

import React from 'react';
import { Link } from 'react-router-dom';
import { checkEntitlement } from '../../lib/entitlements';
import YourOpportunities from './YourOpportunities';

// ADR-044 clarifier — the dashboard presents decision-support signals; the
// investor makes the decision. (Matches the analyzer-surface doctrine.)
const SIGNAL_CLARIFIER =
  'Decision-support signals — you make the final decision. Not investment advice.';

const VERDICT_BADGES = [
  { key: 'pursue', label: 'Pursue', cls: 'badge-pursue' },
  { key: 'investigate', label: 'Investigate', cls: 'badge-investigate' },
  { key: 'reject', label: 'Reject', cls: 'badge-reject' },
];

const PIPELINE_STEPS = [
  { key: 'new', label: 'New' },
  { key: 'analyzing', label: 'Analyzing' },
  { key: 'under_review', label: 'Under review' },
  { key: 'active', label: 'Active' },
  { key: 'pass', label: 'Pass' },
];

const DEAL_SCORES = ['A', 'B', 'C', 'D', 'F'];

const sectionTitleStyle = {
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--text-lg)',
  fontWeight: 'var(--weight-bold)',
  color: 'var(--color-white)',
  margin: 0,
};

const labelStyle = {
  display: 'block',
  fontSize: 'var(--text-xs)',
  textTransform: 'uppercase',
  letterSpacing: 'var(--tracking-wide)',
  color: 'var(--color-steel)',
  marginBottom: 'var(--space-1)',
};

const valueStyle = {
  fontFamily: 'var(--font-display)',
  fontSize: 'var(--text-3xl)',
  fontWeight: 'var(--weight-black)',
  color: 'var(--color-amber)',
};

function Stat({ label, value }) {
  return (
    <div className="card-stat" style={{ textAlign: 'left' }}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value}</span>
    </div>
  );
}

function Section({ title, testId, children, footnote }) {
  return (
    <section data-testid={testId} className="card" style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <h2 style={sectionTitleStyle}>{title}</h2>
      {children}
      {footnote ? (
        <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--color-steel)' }}>{footnote}</p>
      ) : null}
    </section>
  );
}

function Row({ label, value, to }) {
  const body = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'var(--space-2) 0',
        borderBottom: '1px solid var(--color-navy-border)',
      }}
    >
      <span style={{ color: 'var(--color-steel-lt)' }}>{label}</span>
      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 'var(--weight-bold)', color: 'var(--color-white)' }}>
        {value}
      </span>
    </div>
  );
  return to ? (
    <Link to={to} style={{ textDecoration: 'none' }}>
      {body}
    </Link>
  ) : (
    body
  );
}

function hasActivity(m) {
  return (
    (m.analysisVolume?.total || 0) > 0 ||
    (m.verdicts?.total || 0) > 0 ||
    (m.conversion?.total || 0) > 0 ||
    (m.recentAnalyses?.length || 0) > 0 ||
    PIPELINE_STEPS.some((s) => (m.pipelineByStatus?.[s.key] || 0) > 0)
  );
}

function EmptyState({ qualificationEnabled = false }) {
  return (
    <div
      data-testid="dash-empty"
      className="card"
      style={{ textAlign: 'center', padding: 'var(--space-12) var(--space-6)' }}
    >
      <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--weight-bold)', color: 'var(--color-white)', marginBottom: 'var(--space-3)' }}>
        No analyses yet
      </div>
      <p style={{ fontSize: 'var(--text-base)', color: 'var(--color-steel-lt)', maxWidth: 520, margin: '0 auto', marginBottom: 'var(--space-6)' }}>
        {qualificationEnabled ? (
          <>
            Your command center fills in as you work. Qualify an opportunity or run a property in the{' '}
            <strong style={{ color: 'var(--color-amber)' }}>Deal Analyzer</strong> and your signals appear here.
          </>
        ) : (
          <>
            Your command center fills in as you work. Run a property in the{' '}
            <strong style={{ color: 'var(--color-amber)' }}>Deal Analyzer</strong> and your signals appear here.
          </>
        )}
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'center', flexWrap: 'wrap' }}>
        {/* /qualify is build-time feature-flagged (App.js / Layout.js). Only
            surface the CTA when the route is actually mounted, or it navigates
            to an unmounted route and (no catch-all) renders a blank screen. */}
        {qualificationEnabled && (
          <Link to="/qualify" className="btn btn-primary btn-lg">Qualify an opportunity</Link>
        )}
        <Link to="/deal-analyzer" className="btn btn-secondary btn-lg">Open the Deal Analyzer</Link>
      </div>
    </div>
  );
}

export default function DashboardView({ metrics, profile = null, qualificationEnabled = false }) {
  const m = metrics || {};
  const av = m.analysisVolume || { total: 0, last7: 0, last30: 0 };
  const verdicts = m.verdicts || { pursue: 0, investigate: 0, reject: 0, total: 0 };
  const conversion = m.conversion || { savedAsLead: 0, overridden: 0, total: 0 };
  const pipeline = m.pipelineByStatus || {};
  const dealScores = m.dealScoreDistribution || {};
  const hotspots = m.hotspots || [];
  const reviewNext = m.reviewNext || [];
  const recentAnalyses = m.recentAnalyses || [];
  const trialUsage = m.trialUsage || null;
  // BL-048 (ADR-068) — the server OMITS m.opportunities entirely when the
  // feature is off for the user (fail-closed, server-authoritative), so mounting
  // on its presence is the display gate; YourOpportunities renders null when absent.
  const opportunitiesPanel = m.opportunities || null;

  // Advisory only — the server is the boundary. Used to soft-hint a
  // Professional capability, never to enforce.
  const canLeaseLeverage = profile ? checkEntitlement(profile, 'lease_leverage') : false;

  const header = (
    <header style={{ display: 'grid', gap: 'var(--space-1)' }}>
      <h1 style={{ margin: 0 }}>Investor Intelligence</h1>
      <span style={{ ...labelStyle, marginBottom: 0, color: 'var(--color-amber)' }}>Opportunity Signals</span>
      <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-steel-lt)' }}>{SIGNAL_CLARIFIER}</p>
    </header>
  );

  if (!hasActivity(m)) {
    return (
      <div style={{ padding: 'var(--space-5)', display: 'grid', gap: 'var(--space-5)' }}>
        {header}
        <EmptyState qualificationEnabled={qualificationEnabled} />
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--space-5)', display: 'grid', gap: 'var(--space-5)' }}>
      {header}

      <YourOpportunities panel={opportunitiesPanel} />

      {trialUsage ? (
        <section
          data-testid="dash-trial"
          className="card-accent"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-4)', padding: 'var(--space-4)' }}
        >
          <span style={{ ...sectionTitleStyle, fontSize: 'var(--text-base)' }}>Trial usage</span>
          <span style={{ color: 'var(--color-steel-lt)' }}>
            {trialUsage.remaining} of {trialUsage.limit} analyses remaining
          </span>
        </section>
      ) : null}

      {/* 39.2 — Activity & analysis metrics */}
      <Section title="Activity" testId="dash-overview">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-3)' }}>
          <Stat label="Analyses (all time)" value={av.total} />
          <Stat label="Last 7 days" value={av.last7} />
          <Stat label="Last 30 days" value={av.last30} />
        </div>
        <div>
          <span style={labelStyle}>Deal-score signals</span>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            {DEAL_SCORES.map((s) => (
              <span key={s} className="badge badge-analyzing" style={{ fontFamily: 'var(--font-mono)' }}>
                {s}: {dealScores[s] || 0}
              </span>
            ))}
          </div>
        </div>
        {recentAnalyses.length > 0 && (
          <div>
            <span style={labelStyle}>Recent analyses</span>
            {recentAnalyses.map((a) => (
              <Row
                key={a.id}
                to="/my-analyses"
                label={a.label}
                value={`${a.dealScore || '—'} · ${a.recommendation || '—'}`}
              />
            ))}
          </div>
        )}
      </Section>

      {/* 39.3 — Decision metrics (Pursue / Investigate / Reject) */}
      <Section
        title="Decisions"
        testId="dash-decisions"
        footnote={`${conversion.savedAsLead} saved as leads · ${conversion.overridden} overridden — signals from your own qualifications.`}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-3)' }}>
          {VERDICT_BADGES.map((v) => (
            <div key={v.key} className="card-stat" style={{ textAlign: 'left' }}>
              <span className={`badge ${v.cls}`}>{v.label}</span>
              <span style={{ ...valueStyle, marginTop: 'var(--space-2)' }}>{verdicts[v.key] || 0}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* 39.4 — Opportunity pipeline */}
      <Section title="Pipeline" testId="dash-pipeline" footnote="Your leads by stage. Open the pipeline to act on them.">
        {PIPELINE_STEPS.map((s) => (
          <Row key={s.key} to="/leads" label={s.label} value={pipeline[s.key] || 0} />
        ))}
      </Section>

      {/* 39.5 — Market hotspots (user-derived; signal-only) */}
      <Section
        title="Market Signals"
        testId="dash-hotspots"
        footnote="Where your own analyzed opportunities concentrate. A signal about your activity — not a market forecast or advice."
      >
        {hotspots.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--color-muted)', fontStyle: 'italic' }}>
            Market signals appear after you analyze opportunities in a market.
          </p>
        ) : (
          hotspots.map((h) => (
            <Row
              key={h.market}
              label={h.market}
              value={`${h.count} opps · P${h.pursue} I${h.investigate} R${h.reject}${h.avgScore != null ? ` · avg ${h.avgScore}` : ''}`}
            />
          ))
        )}
      </Section>

      {/* 39.6 — Review next */}
      <Section
        title="Review next"
        testId="dash-review-next"
        footnote="Your highest-signal opportunities not yet saved or acted on."
      >
        {reviewNext.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--color-muted)', fontStyle: 'italic' }}>
            Nothing waiting — qualified opportunities you haven’t acted on will surface here.
          </p>
        ) : (
          reviewNext.map((r) => (
            <Row
              key={r.id}
              to={qualificationEnabled ? '/qualify' : undefined}
              label={r.label}
              value={`${r.recommendation}${r.qualificationScore != null ? ` · ${r.qualificationScore}` : ''}`}
            />
          ))
        )}
      </Section>

      {!canLeaseLeverage && (
        <p style={{ margin: 0, fontSize: 'var(--text-xs)', color: 'var(--color-steel)' }}>
          Professional unlocks lease-leverage intelligence. <Link to="/pricing">See plans</Link>.
        </p>
      )}
    </div>
  );
}
